import {action, observable} from 'mobx';
import {actionAsync} from 'mobx-utils';
import {IDocumentUploadApiService} from '@services/document-upload-api-service';
import type {RequestStatus} from '@models/request-status';
import {IErrorStore} from '@screens/review-section2/document-review/review-errors';
import {IRemoteRepresentativeService} from '@services/remote-representative-service';
import {DocumentUploadResponse, WorkStatus} from '@models/onboarding-review/section2-document-upload';
import {RemoteI9Status, RemoteI9StatusDetails} from '@dtos/remote-i9-status';
import {ReceiptDocumentDto} from '@dtos/receipt-documents';
import {RemoteI9Type} from '@dtos/remote-i9-type';
import {
    I9DocumentResultType,
    IDocumentUploadValidationApiService
} from '@services/document-upload-validation-api-service';
import {IPersonPidEncryptionService} from '@services/onboarding-review/person-pid-encryption-service';
import {I9DocumentInfoDto, UploadDocumentRequest} from '@swipejobs/i9-document-validations-react';
import {IDocumentUpdateApiService} from '@services/document-update-api-service';
import type { UpdateDocumentNumbersRequest } from '@dtos/update-documents';

export interface ISection2DocumentUploadStore extends IErrorStore {
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly workStatus: WorkStatus | undefined;
    readonly i9StatusDetails?: RemoteI9StatusDetails | {};
    readonly receiptUploadDocument: I9DocumentInfoDto[];
    readonly documentDetails: I9DocumentResultType;
    handleArchiveReceipts: () => Promise<void>;

    initialise(): void;

    uploadDocumentsAsync(
        requestList: UploadDocumentRequest[],
        onDocumentUploaded?: (
            documentUploadResponse: DocumentUploadResponse[],
            personPidEncryptionService: IPersonPidEncryptionService
        ) => Promise<void>
    ): Promise<void>;

    updateDocumentNumbersAsync(request: UpdateDocumentNumbersRequest): Promise<void>;
    getDocumentDetailsByCitizenshipAsync(workStatus: WorkStatus): Promise<void>;
    getReceiptDocuments(): Promise<void>;
}

// prettier-ignore
export class Section2DocumentUploadStore implements ISection2DocumentUploadStore {
    @observable
    isLoading: boolean = false;
    @observable
    isError: boolean = false;
    @observable
    confirmationRequestStatus: RequestStatus = 'inactive';
    @observable
    workStatus: WorkStatus | undefined;
    @observable
    i9StatusDetails = {
    };
    @observable
    receiptUploadDocument = [] as I9DocumentInfoDto[];
    @observable
    documentDetails: I9DocumentResultType | any = {};

    constructor(
        private documentUploadApiService: IDocumentUploadApiService,
        private remoteRepresentativeService: IRemoteRepresentativeService,
        private documentUploadValidationApiService: IDocumentUploadValidationApiService,
        private personPidEncryptionService: IPersonPidEncryptionService,
        private documentUpdateApiService: IDocumentUpdateApiService
    ) {}

    @action.bound
    async getWorkStatus() {
        try {
            this.isError = false;
            const statusResponse =
                await this.remoteRepresentativeService.getStatusAsync();
            this.workStatus = statusResponse.workStatus;
            this.i9StatusDetails = statusResponse;
            if (statusResponse.receiptReplacementDocumentRequired) {
                const receiptDoc =
                    await this.remoteRepresentativeService.getDocsForReceipts();
                this.receiptUploadDocument = receiptDoc;
            }
        } catch (error) {
            console.error(error);
            this.isError = true;
        }
    }

    @actionAsync
    async handleArchiveReceipts() {
        try {
            this.isLoading = true;
            this.confirmationRequestStatus = 'pending';
            await this.remoteRepresentativeService.archiveI9Documents({
                archiveReceiptDocuments: false,
                archiveSection2Documents: true
            });
            await this.getWorkStatus();
            this.confirmationRequestStatus = 'success';
        } catch (err) {
            this.confirmationRequestStatus = 'error';
            this.isError = true;
            console.error(err);
        } finally {
            this.isLoading = false;
        }
    }

    @action.bound
    async initialise(): Promise<void> {
        this.confirmationRequestStatus = 'inactive';
        await this.getWorkStatus();
    }

    @actionAsync
    async updateDocumentNumbersAsync(
        request: UpdateDocumentNumbersRequest
    ): Promise<void> {
        this.isLoading = true;
        this.confirmationRequestStatus = 'pending';
        try {
            await this.documentUpdateApiService.updateDocumentNumbersAsync(
                request
            );
        } catch (error) {
            console.error(error);
            this.confirmationRequestStatus = 'error';
        } finally {
            this.isLoading = false;
        }
    }

    @actionAsync
    async uploadDocumentsAsync(
        requestList: UploadDocumentRequest[],
        onDocumentUploaded?: (
            documentUploadResponse: DocumentUploadResponse[],
            personPidEncryptionService: IPersonPidEncryptionService
        ) => Promise<void>
    ) {
        this.isLoading = true;
        this.confirmationRequestStatus = 'pending';

        try {
            const documentsUploadResponse = await Promise.all(
                requestList?.map((request) =>
                    this.documentUploadApiService.uploadDocuments(request)
                )
            );
            // Once docs successfully uploaded, it can call this call back method
            // with the response so further api calls or logics can be called
            // e.g.: used for uploading doc numbers afterwards in new doc upload feature
            await onDocumentUploaded?.(
                documentsUploadResponse,
                this.personPidEncryptionService
            );
            this.confirmationRequestStatus = 'success';
        } catch (error) {
            console.error(error);
            this.confirmationRequestStatus = 'error';
        } finally {
            this.isLoading = false;
        }
    }

    @actionAsync
    async getDocumentDetailsByCitizenshipAsync(workStatus: WorkStatus) {
        this.isLoading = true;
        this.confirmationRequestStatus = 'pending';

        try {
            const response =
                await this.documentUploadValidationApiService.getDocumentDetailsByCitizenship(
                    workStatus
                );
            this.documentDetails = response;

            this.confirmationRequestStatus = 'success';
        } catch (error) {
            console.error(error);
            this.confirmationRequestStatus = 'error';
        } finally {
            this.isLoading = false;
        }
    }

    @actionAsync
    async getReceiptDocuments() {
        this.isLoading = true;
        this.confirmationRequestStatus = 'pending';

        try {
            const receiptDoc =
                    await this.remoteRepresentativeService.getDocsForReceipts();
                    this.receiptUploadDocument = receiptDoc;

            this.confirmationRequestStatus = 'success';
        } catch (error) {
            console.error(error);
            this.confirmationRequestStatus = 'error';
        } finally {
            this.isLoading = false;
        }
    }
}



// WEBPACK FOOTER //
// ./src/stores/onboarding-review/section2-document-upload-store.ts